import React from 'react';
import { Box, Typography } from '@mui/material';
import { formatMoney } from '../utilities/helper/money.helper';

import { palette } from '../theme';
/** Colour carries meaning here, so there are only three tones and each says something. */
export const STAT_TONES = {
  neutral: { accent: palette.line, text: 'text.primary' },
  owed: { accent: palette.error.main, text: palette.error.main },
  credit: { accent: palette.info.main, text: palette.info.main }
} as const;

export type StatTone = keyof typeof STAT_TONES;

/**
 * One figure, with an accent only when the figure means something is outstanding.
 *
 * Four identically weighted white tiles made the number the screen exists for - what is
 * still owed - look exactly like the three that are only context. Nothing owed gets no
 * accent: an admin should not have to read a zero to find out it is fine.
 */
const StatTile: React.FC<{
  label: string;
  value: number;
  tone?: StatTone;
  /**
   * Which period the figure covers. A row that silently mixes an all-time total with
   * three monthly ones reads as four of the same thing, and is then misread.
   */
  caption?: string;
  footer?: React.ReactNode;
}> = ({ label, value, tone = 'neutral', caption, footer }) => {
  const { accent, text } = STAT_TONES[tone];
  return (
    <Box
      sx={{
        border: `1px solid ${palette.line}`,
        borderLeft: `4px solid ${accent}`,
        borderRadius: 4,
        px: 3,
        py: 2,
        flex: 1,
        minWidth: 0
      }}
    >
      <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 0.5 }}>{label}</Typography>
      <Typography sx={{ fontSize: 20, fontFamily: 'Poppins', fontWeight: tone === 'neutral' ? 400 : 700, color: text }}>
        {/* the currency is stated once above the row rather than four times across it */}
        {formatMoney(value)}
      </Typography>
      {caption && <Typography sx={{ fontSize: 11, color: 'text.secondary', mt: 0.5 }}>{caption}</Typography>}
      {footer}
    </Box>
  );
};

export default StatTile;
